import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import {
  getApiErrorMessage,
  getErrorStatus,
  getPacientes,
  Paciente,
  PacienteFilters,
} from "src/apis/prontuario";
import PageContainer from "src/pages/components/PageContainer";
import FilterButtons from "src/pages/components/SearchAndResetButtons";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import ProntuarioDrawer from "src/pages/components/prontuario/ProntuarioDrawer";
import { formatEmpty } from "src/pages/components/prontuario/utils";
import { isUserPsychologist } from "src/utils/auth";
import { formatDateToBR } from "src/utils/date";
import { Id } from "src/utils/types";

const initialFilters: PacienteFilters = {
  q: "",
  empresa: "",
};

const Pacientes: FC = () => {
  const [filters, setFilters] = useState<PacienteFilters>(initialFilters);
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [selectedColaboradorId, setSelectedColaboradorId] = useState<Id>(undefined);

  const fetchPacientes = useCallback(async (currentFilters: PacienteFilters) => {
    setLoading(true);

    try {
      const response = await getPacientes(currentFilters);
      setPacientes(response);
      setError("");
    } catch (err: unknown) {
      setError(
        getErrorStatus(err) === 403
          ? "Acesso não autorizado."
          : getApiErrorMessage(err, "Não foi possível carregar pacientes.")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const searchTimeout = window.setTimeout(() => {
      fetchPacientes(filters);
    }, filters.q ? 300 : 0);

    return () => window.clearTimeout(searchTimeout);
  }, [filters, fetchPacientes]);

  const updateFilter = (field: keyof PacienteFilters, value: string) => {
    setFilters((previousFilters) => ({
      ...previousFilters,
      [field]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  return (
    <PageContainer title="Pacientes" error={error} guard={isUserPsychologist}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <TextField
          label="Busca por nome"
          value={filters.q}
          onChange={(event) => updateFilter("q", event.target.value)}
        />
        <TextField
          label="Empresa"
          value={filters.empresa}
          onChange={(event) => updateFilter("empresa", event.target.value)}
        />
        <FilterButtons
          isLoading={loading}
          search={() => fetchPacientes(filters)}
          resetState={resetFilters}
        />
      </Box>

      {loading ? (
        <SkeletonLoad variant="table" rows={6} columns={6} />
      ) : pacientes.length === 0 ? (
        <Alert severity="info">Nenhum paciente encontrado para o seu usuário.</Alert>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Nome do paciente</strong></TableCell>
                <TableCell><strong>Empresa</strong></TableCell>
                <TableCell><strong>Setor</strong></TableCell>
                <TableCell><strong>Cargo</strong></TableCell>
                <TableCell><strong>Último atendimento</strong></TableCell>
                <TableCell><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pacientes.map((paciente) => (
                <TableRow key={String(paciente.id_colaborador)}>
                  <TableCell>{formatEmpty(paciente.nome)}</TableCell>
                  <TableCell>{formatEmpty(paciente.empresa_nome)}</TableCell>
                  <TableCell>{formatEmpty(paciente.setor)}</TableCell>
                  <TableCell>{formatEmpty(paciente.cargo)}</TableCell>
                  <TableCell>{formatDateToBR(paciente.ultimo_atendimento_data)}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<MedicalServicesIcon />}
                      onClick={() => setSelectedColaboradorId(paciente.id_colaborador)}
                    >
                      Abrir prontuário
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ProntuarioDrawer
        idColaborador={selectedColaboradorId}
        open={Boolean(selectedColaboradorId)}
        onClose={() => setSelectedColaboradorId(undefined)}
        presentation="dialog"
      />
    </PageContainer>
  );
};

export default Pacientes;
